import { doc, getDoc } from "firebase/firestore";
import config from "../../config/environment";
import { db } from "../../firebase";
import { applyDevTestLobbyOverride, isDevBuild } from "./devMeetingLobby";
import { isRoomOwnerById } from "../../utils/sessionMeeting";
import { isLiveKitOpenTestRoom } from "../../utils/livekitOpenTestRoom";
import type { SessionSettingsResponse } from "./types";

/**
 * Complementa validateMeetingEntrance quando a function DEV está desatualizada
 * ou o usuário é convidado / sala pública antes do Dyte existir.
 * Bypass de sala de teste: devMeetingLobby (somente REACT_APP_ENVIRONMENT=development).
 */
export async function applyLobbyAccessFallback(
    response: SessionSettingsResponse,
    currentRoomId: string,
    userId: string,
): Promise<SessionSettingsResponse> {
    const withDevOverride = applyDevTestLobbyOverride(response, currentRoomId);
    if (withDevOverride.devTestOpen) {
        return withDevOverride;
    }

    if (
        response.canAccessRoom ||
        response.dyteMeetingAlreadyExists ||
        response.isRoomModerator ||
        response.livekitOpenTest ||
        isLiveKitOpenTestRoom(currentRoomId) ||
        isRoomOwnerById(currentRoomId, userId)
    ) {
        if (isLiveKitOpenTestRoom(currentRoomId)) {
            return {
                ...response,
                canAccessRoom: true,
                entryApproved: true,
                userHasCredits: true,
                isRoomOwner: false,
                livekitOpenTest: true,
            };
        }
        if (isRoomOwnerById(currentRoomId, userId) && !response.canAccessRoom) {
            return {
                ...response,
                canAccessRoom: true,
                entryApproved: true,
                isRoomModerator: true,
                isRoomOwner: true,
                sessionRole: "terapeuta",
            };
        }
        return response;
    }

    try {
        const roomSnap = await getDoc(doc(db, "rooms", currentRoomId));
        if (!roomSnap.exists()) {
            return response;
        }

        const data = roomSnap.data();
        const invited: string[] = Array.isArray(data?.invitedUsers) ? data.invitedUsers : [];

        if (data?.isPublic === true || invited.includes(userId)) {
            return { ...response, canAccessRoom: true };
        }

        if (isDevBuild() && data?.devTestOpen === true) {
            return {
                ...response,
                canAccessRoom: true,
                entryApproved: true,
                devTestOpen: true,
                userHasCredits: true,
            };
        }
    } catch (error) {
        console.warn("Lobby: fallback de acesso à sala falhou", error);
    }

    return response;
}

export function getDevLobbyDebugLabel(): string | null {
    if (!isDevBuild()) {
        return null;
    }
    return `Ambiente DEV · Firebase: ${config.firebase.projectId}`;
}
